import { useState, useEffect, useRef } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useRealtimeInbox } from "../../hooks/useRealtimeInbox";

import {
  Search,
  Send,
  User,
  Plus,
  Radio,
  QrCode,
  ShieldCheck,
  Check,
  CheckCheck,
  Clock,
  Zap,
  Tag as TagIcon,
  Paperclip,
  RefreshCw,
  Smartphone,
  ChevronRight,
  ChevronDown,
  AlertCircle,
  X,
  FileText,
  Image as ImageIcon,
  Film,
  Music,
  Download,
  LayoutTemplate,
  Maximize2,
  Eye,
  Star,
  Pin,
  CornerUpRight,
  Edit2,
  UserCheck,
  ArrowLeft,
  MessageSquare,
} from "lucide-react";
import {
  listConversations,
  assignConversation,
  listPlatformUsers,
  createConversation,
  getConversationMessages,
  sendMessage,
  sendAttachment,
  fetchMessageMedia,
  markConversationRead,
  reactToMessage,
  toggleStarMessage,
  togglePinMessage,
  deleteMessage,
  forwardMessage,
  updateContact,
  listCannedReplies,
  listQRInstances,
  listChannels,
  listTemplates,
  type CurrentUser,
  type Conversation,
  type Message,
  type CannedReply,
  type TemplateItem,
  type PlatformUserItem,
} from "../../lib/api";
import { AudioPlayer } from "../inbox/AudioPlayer";
import { MediaPreviewModal } from "../inbox/MediaPreviewModal";
import { TemplatePickerModal } from "../inbox/TemplatePickerModal";
import { MessageContextMenu } from "../inbox/MessageContextMenu";
import { ForwardMessageModal } from "../inbox/ForwardMessageModal";

export function getContactDisplayName(
  contact?: { first_name?: string | null; last_name?: string | null; phone_e164?: string | null } | null
): string {
  const fullName = [contact?.first_name, contact?.last_name].filter(Boolean).join(" ").trim();
  if (
    !fullName ||
    fullName.startsWith("WhatsApp ") ||
    fullName.startsWith("Contact ") ||
    fullName === "WhatsApp User" ||
    fullName === "WhatsApp Contact"
  ) {
    return "Unknown";
  }
  return fullName;
}

const mediaBlobCache = new Map<string, string>();

function AuthenticatedMedia({
  token,
  message,
  onPreviewMedia,
}: {
  token: string;
  message: Message;
  onPreviewMedia?: (media: {
    url: string;
    type: string;
    name?: string;
    size?: number;
    mime?: string;
  }) => void;
}) {
  const cachedUrl = mediaBlobCache.get(message.id);
  const [url, setUrl] = useState<string | null>(cachedUrl || null);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (mediaBlobCache.has(message.id)) {
      setUrl(mediaBlobCache.get(message.id)!);
      return;
    }
    let cancelled = false;
    fetchMessageMedia(token, message.id)
      .then((blob) => {
        if (cancelled) return;
        const objectUrl = URL.createObjectURL(blob);
        mediaBlobCache.set(message.id, objectUrl);
        setUrl(objectUrl);
      })
      .catch(() => !cancelled && setError(true));
    return () => {
      cancelled = true;
    };
  }, [token, message.id]);

  const thumbnailUrl = message.content?.thumbnail_url;
  const name = message.content?.file_name || `${message.message_type} attachment`;
  const isOutbound = message.direction === "outbound";
  const fileSize = message.content?.file_length;
  const mimeType = message.content?.mimetype;

  const formatFileSize = (bytes?: number) => {
    if (!bytes || bytes <= 0) return "";
    const k = 1024;
    const sizes = ["B", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${(bytes / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`;
  };

  const displayUrl = url || thumbnailUrl;

  if (error && !displayUrl) {
    return (
      <div
        className={`flex items-center gap-2.5 p-2 rounded-lg border text-xs ${
          isOutbound
            ? "bg-black/10 border-emerald-500/30 text-emerald-100"
            : "bg-black/5 border-slate-200 text-slate-600 dark:text-slate-300"
        }`}
      >
        {message.message_type === "image" ? (
          <ImageIcon className="w-4 h-4 shrink-0 text-emerald-500" />
        ) : message.message_type === "video" ? (
          <Film className="w-4 h-4 shrink-0 text-purple-500" />
        ) : (
          <FileText className="w-4 h-4 shrink-0 text-blue-500" />
        )}
        <span className="font-medium capitalize">{name}</span>
        {fileSize ? (
          <span className="text-[10px] opacity-70">({formatFileSize(fileSize)})</span>
        ) : null}
      </div>
    );
  }

  if (message.message_type === "image") {
    if (!displayUrl) {
      return (
        <div className="h-16 min-w-40 animate-pulse rounded-lg bg-black/10 flex items-center justify-center text-[10px] text-slate-400">
          Loading attachment...
        </div>
      );
    }
    return (
      <div
        className="relative group cursor-pointer overflow-hidden rounded-xl"
        onClick={() => onPreviewMedia?.({ url: displayUrl, type: "image", name, size: fileSize, mime: mimeType })}
      >
        <img
          src={displayUrl}
          alt={name}
          className="max-h-72 max-w-full rounded-xl object-cover hover:opacity-95 transition-opacity"
        />
        <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
          <span className="p-1.5 rounded-full bg-black/60 text-white shadow">
            <Maximize2 className="w-4 h-4" />
          </span>
        </div>
      </div>
    );
  }

  if (!url) {
    return (
      <div className="h-16 min-w-40 animate-pulse rounded-lg bg-black/10 flex items-center justify-center text-[10px] text-slate-400">
        Loading attachment...
      </div>
    );
  }

  if (message.message_type === "video") {
    return (
      <div className="relative rounded-xl overflow-hidden max-w-sm">
        <video
          src={url}
          controls
          playsInline
          preload="metadata"
          className="max-h-72 max-w-full rounded-xl bg-black"
        />
        <button
          type="button"
          onClick={() => onPreviewMedia?.({ url, type: "video", name, size: fileSize, mime: mimeType })}
          className="absolute top-2 right-2 p-1.5 rounded-full bg-black/60 text-white hover:bg-black/80 transition-colors shadow"
          title="Fullscreen Preview"
        >
          <Maximize2 className="w-3.5 h-3.5" />
        </button>
      </div>
    );
  }

  if (message.message_type === "audio") {
    return (
      <AudioPlayer
        src={url}
        isOutbound={isOutbound}
        isPtt={Boolean(message.content?.ptt)}
      />
    );
  }

  // Document Card
  const ext = name.split(".").pop()?.toUpperCase() || "DOC";
  const isPdf = name.toLowerCase().endsWith(".pdf") || mimeType?.includes("pdf");

  return (
    <div
      className={`flex items-center gap-3 p-2.5 rounded-xl border transition-all ${
        isOutbound
          ? "bg-emerald-700/50 border-emerald-500/40 text-white"
          : "bg-slate-50 border-slate-200 text-slate-900"
      } min-w-[220px] max-w-xs shadow-2xs`}
    >
      <div
        className={`w-10 h-10 rounded-lg flex flex-col items-center justify-center shrink-0 font-bold text-[10px] shadow-xs ${
          isPdf ? "bg-rose-500 text-white" : "bg-blue-500 text-white"
        }`}
      >
        <FileText className="w-4 h-4" />
        <span className="leading-none mt-0.5">{ext.slice(0, 4)}</span>
      </div>

      <div className="flex-1 min-w-0">
        <p className="font-semibold text-xs truncate" title={name}>
          {name}
        </p>
        <p className="text-[10px] opacity-70 font-mono">
          {fileSize ? formatFileSize(fileSize) : "Document"}
        </p>
      </div>

      <div className="flex items-center gap-1 shrink-0">
        {isPdf && (
          <button
            type="button"
            onClick={() => onPreviewMedia?.({ url, type: "document", name, size: fileSize, mime: mimeType })}
            className="p-1.5 rounded-lg hover:bg-black/10 transition-colors"
            title="Preview PDF"
          >
            <Eye className="w-4 h-4" />
          </button>
        )}
        <a
          href={url}
          download={name}
          className="p-1.5 rounded-lg hover:bg-black/10 transition-colors"
          title="Download Document"
        >
          <Download className="w-4 h-4" />
        </a>
      </div>
    </div>
  );
}

const COUNTRY_CODES = [
  { code: "+91", country: "IN", flag: "🇮🇳", name: "India (+91)" },
  { code: "+1", country: "US", flag: "🇺🇸", name: "USA / Canada (+1)" },
  { code: "+44", country: "GB", flag: "🇬🇧", name: "UK (+44)" },
  { code: "+971", country: "AE", flag: "🇦🇪", name: "UAE (+971)" },
  { code: "+966", country: "SA", flag: "🇸🇦", name: "Saudi Arabia (+966)" },
  { code: "+65", country: "SG", flag: "🇸🇬", name: "Singapore (+65)" },
  { code: "+61", country: "AU", flag: "🇦🇺", name: "Australia (+61)" },
  { code: "+49", country: "DE", flag: "🇩🇪", name: "Germany (+49)" },
  { code: "+33", country: "FR", flag: "🇫🇷", name: "France (+33)" },
  { code: "+880", country: "BD", flag: "🇧🇩", name: "Bangladesh (+880)" },
  { code: "+92", country: "PK", flag: "🇵🇰", name: "Pakistan (+92)" },
  { code: "+977", country: "NP", flag: "🇳🇵", name: "Nepal (+977)" },
  { code: "+94", country: "LK", flag: "🇱🇰", name: "Sri Lanka (+94)" },
  { code: "+60", country: "MY", flag: "🇲🇾", name: "Malaysia (+60)" },
  { code: "+62", country: "ID", flag: "🇮🇩", name: "Indonesia (+62)" },
  { code: "+55", country: "BR", flag: "🇧🇷", name: "Brazil (+55)" },
  { code: "+27", country: "ZA", flag: "🇿🇦", name: "South Africa (+27)" },
  { code: "+234", country: "NG", flag: "🇳🇬", name: "Nigeria (+234)" },
  { code: "+254", country: "KE", flag: "🇰🇪", name: "Kenya (+254)" },
];

function formatPhoneWithCountryCode(raw: string, countryCode: string = "+91"): string {
  const trimmed = raw.trim();
  if (!trimmed) return "";
  if (trimmed.startsWith("+")) return trimmed;
  const digits = trimmed.replace(/\D/g, "");
  const ccDigits = countryCode.replace(/\D/g, "");
  if (digits.startsWith(ccDigits) && digits.length > ccDigits.length + 6) {
    return `+${digits}`;
  }
  return `${countryCode}${digits}`;
}

const SELECTED_CHANNEL_STORAGE_KEY = "wamaster_inbox_selected_channel";

export function UnifiedInboxView({ token, user, tenantId }: { token: string; user: CurrentUser; tenantId?: string }) {
  const queryClient = useQueryClient();
  const effectiveTenantId = user.role === "tenant" ? user.tenant_id : (tenantId && tenantId !== "all" ? tenantId : user.tenant_id);
  const [activeConversationId, setActiveConversationId] = useState<string | null>(null);
  const [selectedChannelId, setSelectedChannelId] = useState<string>(() => {
    try {
      const saved = localStorage.getItem(SELECTED_CHANNEL_STORAGE_KEY);
      return saved && saved !== "all" ? saved : "";
    } catch {
      return "";
    }
  });
  const [filterStatus, setFilterStatus] = useState<string>("all");
  const [filterAssignment, setFilterAssignment] = useState<"all" | "me" | "unassigned">("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCountryCode, setSelectedCountryCode] = useState<string>("+91");
  const [messageInput, setMessageInput] = useState("");
  const [isNewChatOpen, setIsNewChatOpen] = useState(false);
  const [newChatPhone, setNewChatPhone] = useState("");
  const [newChatName, setNewChatName] = useState("");
  const [newChatChannel, setNewChatChannel] = useState<"qr_whatsapp" | "meta_official">("qr_whatsapp");
  const [newChatInitialMessage, setNewChatInitialMessage] = useState("");
  const [showCannedReplies, setShowCannedReplies] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  const [replyingTo, setReplyingTo] = useState<Message | null>(null);
  const [attachment, setAttachment] = useState<File | null>(null);
  const attachmentInputRef = useRef<HTMLInputElement>(null);
  const [activeMediaPreview, setActiveMediaPreview] = useState<{
    url: string;
    type: string;
    name?: string;
    size?: number;
    mime?: string;
  } | null>(null);
  const [highlightedMessageId, setHighlightedMessageId] = useState<string | null>(null);
  const [contextMenu, setContextMenu] = useState<{
    isOpen: boolean;
    message: Message;
    position: { x: number; y: number };
  } | null>(null);
  const [forwardModal, setForwardModal] = useState<{
    isOpen: boolean;
    message: Message | null;
  }>({ isOpen: false, message: null });

  // Inline Contact Name Editing State
  const [editingContactId, setEditingContactId] = useState<string | null>(null);
  const [contactNameInput, setContactNameInput] = useState("");

  useEffect(() => {
    setEditingContactId(null);
  }, [activeConversationId]);

  const scrollToMessage = (msgId: string) => {
    const el = document.getElementById(`msg-${msgId}`);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      setHighlightedMessageId(msgId);
      setTimeout(() => {
        setHighlightedMessageId((curr) => (curr === msgId ? null : curr));
      }, 2500);
    }
  };

  const copyMessageText = (text: string, _id?: string) => {
    navigator.clipboard.writeText(text);
  };

  const handleSendWithText = (text: string) => {
    if (!text.trim() || !activeConversationId || sendMutation.isPending) return;
    sendMutation.mutate({ text: text.trim(), file: null });
  };

  const { isConnected: isRealtimeConnected } = useRealtimeInbox({
    token,
    activeConversationId,
    tenantId: effectiveTenantId,
  });

  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Live QR Instances & Channels Query for top selector
  const qrInstancesQuery = useQuery({
    queryKey: ["qr-instances", effectiveTenantId],
    queryFn: () => listQRInstances(token, effectiveTenantId),
    refetchInterval: isRealtimeConnected ? 15_000 : 5_000,
    refetchIntervalInBackground: true,
  });

  const channelsQuery = useQuery({
    queryKey: ["channels", effectiveTenantId],
    queryFn: () => listChannels(token, undefined, effectiveTenantId),
    refetchInterval: isRealtimeConnected ? 15_000 : 5_000,
    refetchIntervalInBackground: true,
  });

  const qrInstances = qrInstancesQuery.data || [];
  const metaChannels = channelsQuery.data || [];

  // Ensure exactly one channel is selected by default and preserved across refreshes
  useEffect(() => {
    const allChannelOptions: string[] = [
      ...qrInstances.map((i) => `qr_${i.id}`),
      ...metaChannels.map((c) => `meta_${c.id}`),
    ];

    if (allChannelOptions.length === 0) return;

    // Check if the current selectedChannelId is valid among existing channels
    const isValid =
      selectedChannelId &&
      selectedChannelId !== "all" &&
      allChannelOptions.includes(selectedChannelId);

    if (!isValid) {
      // Find preferred single channel: connected QR instance > active Meta channel > first available
      const connectedQR = qrInstances.find((i) => i.status === "connected");
      const activeMeta = metaChannels.find((c) => c.status === "active");
      const preferred = connectedQR
        ? `qr_${connectedQR.id}`
        : activeMeta
        ? `meta_${activeMeta.id}`
        : allChannelOptions[0];

      if (preferred) {
        setSelectedChannelId(preferred);
        try {
          localStorage.setItem(SELECTED_CHANNEL_STORAGE_KEY, preferred);
        } catch {}
      }
    }
  }, [qrInstances, metaChannels, selectedChannelId]);

  // Live Team Users Query for Agent Chat Assignment
  const teamUsersQuery = useQuery({
    queryKey: ["platform-users", effectiveTenantId],
    queryFn: () => listPlatformUsers(token),
  });
  const teamUsers: PlatformUserItem[] = teamUsersQuery.data || [];

  // Chat Assignment Mutation
  const assignMutation = useMutation({
    mutationFn: ({ conversationId, assignedUserId }: { conversationId: string; assignedUserId: string | null }) =>
      assignConversation(token, conversationId, assignedUserId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["inbox", "conversations"] });
    },
  });

  // Live Conversations Query (Instant via Redis Pub/Sub, with 10s fallback)
  const conversationsQuery = useQuery({
    queryKey: ["inbox", "conversations", filterStatus, filterAssignment, effectiveTenantId],
    queryFn: () =>
      listConversations(
        token,
        filterStatus === "all" ? undefined : filterStatus,
        effectiveTenantId,
        filterAssignment === "all" ? undefined : filterAssignment
      ),
    refetchInterval: isRealtimeConnected ? 10_000 : 2_000,
    refetchIntervalInBackground: true,
  });

  const conversations = conversationsQuery.data || [];

  const hasAutoSelectedRef = useRef(false);

  // Automatically select first conversation ONCE on desktop if none selected
  useEffect(() => {
    if (hasAutoSelectedRef.current) return;
    // On mobile, never auto-select so user lands on the conversation list
    if (typeof window !== "undefined" && window.innerWidth < 768) {
      hasAutoSelectedRef.current = true;
      return;
    }
    if (!activeConversationId && conversations.length > 0) {
      const first = conversations[0];
      if (first) {
        setActiveConversationId(first.id);
        hasAutoSelectedRef.current = true;
      }
    }
  }, [conversations, activeConversationId]);

  // Live Messages Query for Active Conversation (Instant via Redis Pub/Sub, with 10s fallback)
  const messagesQuery = useQuery({
    queryKey: ["inbox", "messages", activeConversationId],
    queryFn: () => (activeConversationId ? getConversationMessages(token, activeConversationId) : []),
    enabled: !!activeConversationId,
    staleTime: 60_000,
    gcTime: 10 * 60_000,
    refetchInterval: isRealtimeConnected ? 15_000 : 3_000,
    refetchIntervalInBackground: false,
  });

  // Preload visible conversations so clicking any contact opens in 0ms
  useEffect(() => {
    if (!conversations || conversations.length === 0) return;
    conversations.slice(0, 15).forEach((conv) => {
      queryClient.prefetchQuery({
        queryKey: ["inbox", "messages", conv.id],
        queryFn: () => getConversationMessages(token, conv.id),
        staleTime: 60_000,
      });
    });
  }, [conversations, token, queryClient]);


  const messages = messagesQuery.data || [];

  // Live Canned Replies
  const cannedRepliesQuery = useQuery({
    queryKey: ["inbox", "canned-replies", user.tenant_id],
    queryFn: () => listCannedReplies(token, user.tenant_id),
  });

  const cannedReplies = cannedRepliesQuery.data || [];

  const templatesQuery = useQuery({
    queryKey: ["templates", effectiveTenantId],
    queryFn: () => listTemplates(token, effectiveTenantId),
  });
  const templates = (templatesQuery.data || []).filter(
    (item: TemplateItem) => (item.structure?.channel_type || "qr_whatsapp") === "qr_whatsapp"
  );

  // Auto-scroll messages inside its container only (does not scroll window or sidebar)
  useEffect(() => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }
  }, [messages.length, activeConversationId]);

  // Send Message Mutation
  const sendMutation = useMutation({
    mutationFn: ({ text, file }: { text: string; file: File | null }) => {
      if (file) {
        const mediaType = file.type.startsWith("image/")
          ? "image"
          : file.type.startsWith("video/")
          ? "video"
          : file.type.startsWith("audio/")
          ? "audio"
          : "document";
        return sendAttachment(token, activeConversationId!, {
          file,
          mediaType,
          caption: text.trim(),
          quotedMessageId: replyingTo?.id,
        });
      }
      return sendMessage(token, activeConversationId!, {
        message_type: "text",
        content: { text: text.trim() },
        quoted_message_id: replyingTo?.id,
      });
    },
    onSuccess: () => {
      setMessageInput("");
      setAttachment(null);
      setReplyingTo(null);
      queryClient.invalidateQueries({ queryKey: ["inbox", "messages", activeConversationId] });
      queryClient.invalidateQueries({ queryKey: ["inbox", "conversations"] });
    },
    onError: (err: any) => {
      alert(`Message failed to send: ${err?.message || "Unknown error"}`);
    },
  });

  // Start New Conversation Mutation
  const newChatMutation = useMutation({
    mutationFn: () => {
      const targetInstanceId =
        selectedChannelId && selectedChannelId.startsWith("qr_")
          ? selectedChannelId.replace("qr_", "")
          : undefined;
      return createConversation(
        token,
        {
          phone_e164: formatPhoneWithCountryCode(newChatPhone, selectedCountryCode),
          contact_name: newChatName.trim() || undefined,
          channel_type: newChatChannel,
          channel_instance_id: targetInstanceId,
          initial_message: newChatInitialMessage.trim() || undefined,
        },
        effectiveTenantId
      );
    },
    onSuccess: (newConv) => {
      setFilterStatus("all");
      queryClient.setQueriesData(
        { queryKey: ["inbox", "conversations"] },
        (old: any) => {
          if (!old || !Array.isArray(old)) return [newConv];
          return [newConv, ...old.filter((c: any) => c.id !== newConv.id)];
        }
      );
      queryClient.invalidateQueries({ queryKey: ["inbox", "conversations"] });
      if (newConv?.id) {
        queryClient.invalidateQueries({ queryKey: ["inbox", "messages", newConv.id] });
        setActiveConversationId(newConv.id);
      }
      setIsNewChatOpen(false);
      setNewChatPhone("");
      setNewChatName("");
      setNewChatInitialMessage("");
    },
    onError: (err: any) => {
      alert(err?.message || "Failed to start chat. Please check recipient phone number.");
    },
  });

  const pinnedMessage = messages.slice().reverse().find((m) => m.content?.is_pinned);

  // Message Reaction Mutation
  const reactMutation = useMutation({
    mutationFn: ({ messageId, emoji }: { messageId: string; emoji: string }) =>
      reactToMessage(token, messageId, emoji),
    onSuccess: (data, variables) => {
      queryClient.setQueryData(
        ["inbox", "messages", activeConversationId],
        (old: Message[] | undefined) => {
          if (!old) return old;
          return old.map((m) => {
            if (m.id === variables.messageId) {
              return {
                ...m,
                content: {
                  ...(m.content || {}),
                  reactions: data.reactions,
                },
              };
            }
            return m;
          });
        }
      );
    },
  });

  // Message Star Mutation
  const starMutation = useMutation({
    mutationFn: (messageId: string) => toggleStarMessage(token, messageId),
    onSuccess: (data, messageId) => {
      queryClient.setQueryData(
        ["inbox", "messages", activeConversationId],
        (old: Message[] | undefined) => {
          if (!old) return old;
          return old.map((m) => {
            if (m.id === messageId) {
              return {
                ...m,
                content: {
                  ...(m.content || {}),
                  is_starred: data.is_starred,
                },
              };
            }
            return m;
          });
        }
      );
    },
  });

  // Message Pin Mutation
  const pinMutation = useMutation({
    mutationFn: (messageId: string) => togglePinMessage(token, messageId),
    onSuccess: (data, messageId) => {
      queryClient.setQueryData(
        ["inbox", "messages", activeConversationId],
        (old: Message[] | undefined) => {
          if (!old) return old;
          return old.map((m) => {
            if (m.id === messageId) {
              return {
                ...m,
                content: {
                  ...(m.content || {}),
                  is_pinned: data.is_pinned,
                },
              };
            }
            return m;
          });
        }
      );
    },
  });

  // Message Delete Mutation
  const deleteMutation = useMutation({
    mutationFn: (messageId: string) => deleteMessage(token, messageId),
    onSuccess: (_data, messageId) => {
      queryClient.setQueryData(
        ["inbox", "messages", activeConversationId],
        (old: Message[] | undefined) => {
          if (!old) return old;
          return old.filter((m) => m.id !== messageId);
        }
      );
      queryClient.invalidateQueries({ queryKey: ["inbox", "conversations"] });
    },
  });

  // Update Contact Name Mutation
  const updateContactNameMutation = useMutation({
    mutationFn: async ({ contactId, name }: { contactId: string; name: string }) => {
      const trimmed = name.trim();
      return updateContact(token, contactId, {
        first_name: trimmed || "Unknown",
        last_name: "",
      });
    },
    onSuccess: (updatedContact) => {
      queryClient.setQueriesData({ queryKey: ["inbox", "conversations"] }, (old: any) => {
        if (!Array.isArray(old)) return old;
        return old.map((conv: Conversation) => {
          if (conv.contact_id === updatedContact.id) {
            return {
              ...conv,
              contact: {
                ...conv.contact,
                ...updatedContact,
              },
            };
          }
          return conv;
        });
      });
      queryClient.invalidateQueries({ queryKey: ["inbox", "conversations"] });
      queryClient.invalidateQueries({ queryKey: ["contacts"] });
      setEditingContactId(null);
    },
    onError: (err: any) => {
      alert(err?.message || "Failed to update contact name");
    },
  });

  // Forward Message Handler
  const handleForwardMessage = async (targetConversationId: string) => {
    if (!forwardModal.message) return;
    await forwardMessage(token, forwardModal.message.id, targetConversationId);
    queryClient.invalidateQueries({ queryKey: ["inbox", "conversations"] });
    if (targetConversationId === activeConversationId) {
      queryClient.invalidateQueries({ queryKey: ["inbox", "messages", activeConversationId] });
    }
  };

  const activeConversation = conversations.find((c) => c.id === activeConversationId);

  const filteredConversations = conversations.filter((c) => {
    const contactName = getContactDisplayName(c.contact);
    const contactPhone = c.contact?.phone_e164 || "";
    const preview = c.last_message_preview || "";
    const q = searchQuery.toLowerCase().trim();
    const formattedSearch = formatPhoneWithCountryCode(searchQuery, selectedCountryCode);
    const matchesSearch =
      !q ||
      contactName.toLowerCase().includes(q) ||
      contactPhone.toLowerCase().includes(q) ||
      contactPhone.includes(formattedSearch) ||
      preview.toLowerCase().includes(q);
    if (!matchesSearch) return false;

    if (selectedChannelId && selectedChannelId.startsWith("qr_")) {
      const targetId = selectedChannelId.replace("qr_", "");
      return (
        c.channel_type === "qr_whatsapp" &&
        (!c.channel_instance_id || c.channel_instance_id === targetId)
      );
    }
    if (selectedChannelId && selectedChannelId.startsWith("meta_")) {
      const targetId = selectedChannelId.replace("meta_", "");
      return (
        c.channel_type === "meta_official" &&
        (!c.channel_instance_id || c.channel_instance_id === targetId)
      );
    }
    return true;
  });

  function handleSend(e: React.FormEvent) {
    e.preventDefault();
    if ((!messageInput.trim() && !attachment) || !activeConversationId || sendMutation.isPending) return;
    sendMutation.mutate({ text: messageInput, file: attachment });
  }

  function insertCannedReply(reply: CannedReply) {
    setMessageInput(reply.content);
    setShowCannedReplies(false);
  }

  const isSearchPhoneNumber = searchQuery.replace(/[^\d]/g, "").length >= 4;
  const searchFormattedPhone = formatPhoneWithCountryCode(searchQuery, selectedCountryCode);

  function handleStartChatFromSearch() {
    setNewChatPhone(searchQuery.trim());
    if (selectedChannelId.startsWith("meta_")) {
      setNewChatChannel("meta_official");
    } else {
      setNewChatChannel("qr_whatsapp");
    }
    setIsNewChatOpen(true);
  }

  const activeQR = qrInstances.find((i) => `qr_${i.id}` === selectedChannelId);
  const activeMeta = metaChannels.find((c) => `meta_${c.id}` === selectedChannelId);

  return (
    <div className="h-[calc(100dvh-75px)] md:h-[calc(100vh-130px)] flex flex-col space-y-2 md:space-y-3">
      {/* Top Header - hidden on mobile when viewing active chat */}
      <div className={`${activeConversationId ? "hidden md:flex" : "flex"} flex-col sm:flex-row justify-between items-start sm:items-center gap-2 md:gap-3`}>
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight flex items-center gap-2">
            <Radio className="w-6 h-6 text-emerald-600" />
            Live Inbox
            <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
              Live Real-Time
            </span>
          </h1>
          <p className="text-xs text-slate-500">
            Real-time chat with Meta Verified WABA and Direct QR Multi-Device WhatsApp sessions.
          </p>
        </div>

        {/* Top Channel Selector & Actions */}
        <div className="flex flex-wrap items-center gap-2.5">
          {/* Active Sending Channel Switcher */}
          <div className="flex items-center gap-2 bg-white px-3 py-1.5 rounded-xl border border-slate-200 shadow-2xs">
            <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wider flex items-center gap-1 shrink-0">
              <Smartphone className="w-3.5 h-3.5 text-emerald-600" />
              Channel:
            </span>
            <select
              value={selectedChannelId}
              onChange={(e) => {
                const val = e.target.value;
                setSelectedChannelId(val);
                try {
                  localStorage.setItem(SELECTED_CHANNEL_STORAGE_KEY, val);
                } catch {}
                if (val.startsWith("meta_")) {
                  setNewChatChannel("meta_official");
                } else {
                  setNewChatChannel("qr_whatsapp");
                }
              }}
              className="text-xs font-semibold text-slate-800 bg-transparent border-none outline-none cursor-pointer pr-1"
            >
              {qrInstances.length === 0 && metaChannels.length === 0 && (
                <option value="">No channels configured</option>
              )}
              {qrInstances.length > 0 && (
                <optgroup label="📱 QR WhatsApp Instances">
                  {qrInstances.map((inst) => (
                    <option key={inst.id} value={`qr_${inst.id}`}>
                      📱 {inst.name} ({inst.phone_number ? `+${inst.phone_number}` : "No number"}) {inst.status === "connected" ? "🟢 [Connected]" : "⚪ [Offline]"}
                    </option>
                  ))}
                </optgroup>
              )}
              {metaChannels.length > 0 && (
                <optgroup label="🛡️ Meta Official WABA Channels">
                  {metaChannels.map((ch) => (
                    <option key={ch.id} value={`meta_${ch.id}`}>
                      🛡️ {ch.name} ({ch.phone_number}) {ch.status === "active" ? "🟢 [Active]" : "⚪ [Offline]"}
                    </option>
                  ))}
                </optgroup>
              )}
            </select>
          </div>

          <button
            onClick={() => {
              if (selectedChannelId.startsWith("meta_")) {
                setNewChatChannel("meta_official");
              } else {
                setNewChatChannel("qr_whatsapp");
              }
              setIsNewChatOpen(true);
            }}
            className="btn-primary text-xs py-2 px-3.5 flex items-center gap-1.5 shadow-2xs"
          >
            <Plus className="w-3.5 h-3.5" />
            <span>New Chat</span>
          </button>
        </div>
      </div>

      {/* Main Flex Layout (WhatsApp Web standard) */}
      <div className="flex-1 min-h-0 flex flex-col md:flex-row bg-white border border-slate-200 rounded-xl overflow-hidden shadow-2xs">
        
        {/* Left Column: Thread List (Compact 320-350px on desktop, full-width on mobile when no active chat) */}
        <div className={`${activeConversationId ? "hidden md:flex" : "flex"} w-full md:w-[320px] lg:w-[350px] shrink-0 border-r border-slate-200 flex-col h-full min-h-0 bg-slate-50/50`}>

          
          {/* Search & Filter */}
          <div className="p-3 border-b border-slate-200 space-y-2 bg-white">
            <div className="flex items-center gap-1.5">
              <select
                value={selectedCountryCode}
                onChange={(e) => setSelectedCountryCode(e.target.value)}
                className="h-8 pl-1.5 pr-1 rounded-lg border border-slate-200 text-[11px] bg-slate-100 font-mono font-semibold focus:outline-none focus:border-emerald-600 shrink-0 cursor-pointer"
                title="Default Country Code Prefix"
              >
                {COUNTRY_CODES.map((c) => (
                  <option key={c.code} value={c.code}>
                    {c.flag} {c.code}
                  </option>
                ))}
              </select>
              <div className="relative flex-1">
                <Search className="w-3.5 h-3.5 text-slate-400 absolute left-2.5 top-2.5" />
                <input
                  type="text"
                  placeholder="Search name or enter phone..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && searchQuery.trim()) {
                      handleStartChatFromSearch();
                    }
                  }}
                  className="w-full pl-7 pr-2 py-1.5 rounded-lg border border-slate-200 text-xs bg-slate-50 focus:outline-none focus:border-emerald-600"
                />
              </div>
            </div>

            {/* Controlled Assignment Filters */}
            <div className="grid grid-cols-3 gap-1 bg-slate-100 p-1 rounded-lg text-xs">
              <button
                type="button"
                onClick={() => setFilterAssignment("all")}
                className={`py-1 px-1.5 rounded-md text-center text-[11px] font-semibold transition-all flex items-center justify-center gap-1 cursor-pointer truncate ${
                  filterAssignment === "all"
                    ? "bg-white text-slate-900 shadow-2xs font-bold"
                    : "text-slate-500 hover:text-slate-900"
                }`}
              >
                <MessageSquare className="w-3 h-3 shrink-0" />
                <span>All</span>
              </button>
              <button
                type="button"
                onClick={() => setFilterAssignment("me")}
                className={`py-1 px-1.5 rounded-md text-center text-[11px] font-semibold transition-all flex items-center justify-center gap-1 cursor-pointer truncate ${
                  filterAssignment === "me"
                    ? "bg-white text-emerald-700 shadow-2xs font-bold"
                    : "text-slate-500 hover:text-slate-900"
                }`}
              >
                <User className="w-3 h-3 shrink-0" />
                <span>Mine</span>
              </button>
              <button
                type="button"
                onClick={() => setFilterAssignment("unassigned")}
                className={`py-1 px-1.5 rounded-md text-center text-[11px] font-semibold transition-all flex items-center justify-center gap-1 cursor-pointer truncate ${
                  filterAssignment === "unassigned"
                    ? "bg-white text-amber-700 shadow-2xs font-bold"
                    : "text-slate-500 hover:text-slate-900"
                }`}
              >
                <Clock className="w-3 h-3 shrink-0" />
                <span>Pending</span>
              </button>
            </div>

            <div className="flex items-center gap-1 text-xs">
              <button
                onClick={() => setFilterStatus("all")}
                className={`px-2 py-1 rounded-md font-semibold text-[11px] ${
                  filterStatus === "all" ? "bg-emerald-50 text-emerald-700 font-bold" : "text-slate-500 hover:text-slate-900"
                }`}
              >
                All ({conversations.length})
              </button>
              <button
                onClick={() => setFilterStatus("open")}
                className={`px-2 py-1 rounded-md font-semibold text-[11px] ${
                  filterStatus === "open" ? "bg-emerald-50 text-emerald-700 font-bold" : "text-slate-500 hover:text-slate-900"
                }`}
              >
                Open
              </button>
              <button
                onClick={() => setFilterStatus("unread")}
                className={`px-2 py-1 rounded-md font-semibold text-[11px] ${
                  filterStatus === "unread" ? "bg-emerald-50 text-emerald-700 font-bold" : "text-slate-500 hover:text-slate-900"
                }`}
              >
                Unread
              </button>
            </div>
          </div>

          {/* Thread Items List */}
          <div className="flex-1 overflow-y-auto divide-y divide-slate-100">
            {/* Direct Start Chat from Search Query */}
            {isSearchPhoneNumber && (
              <button
                onClick={handleStartChatFromSearch}
                className="w-full p-3 text-left flex items-center gap-3 bg-emerald-50/80 hover:bg-emerald-100 border-b border-emerald-200 transition-colors"
              >
                <div className="w-9 h-9 rounded-full bg-emerald-600 text-white flex items-center justify-center font-bold text-xs shrink-0 shadow-2xs">
                  <Plus className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <span className="font-bold text-emerald-950 text-xs">Start Direct Chat</span>
                    <span className="text-[10px] px-1.5 py-0.2 rounded bg-emerald-200 text-emerald-800 font-bold">New</span>
                  </div>
                  <p className="text-[11px] text-emerald-700 font-mono truncate">
                    Direct message: {searchFormattedPhone}
                  </p>
                </div>
                <ChevronRight className="w-4 h-4 text-emerald-600" />
              </button>
            )}

            {conversationsQuery.isLoading ? (
              <div className="p-8 text-center text-xs text-slate-400 flex flex-col items-center justify-center gap-2">
                <RefreshCw className="w-5 h-5 text-emerald-600 animate-spin" />
                <span>Loading conversations...</span>
              </div>
            ) : filteredConversations.length === 0 ? (
              <div className="p-4 text-center text-xs text-slate-400 space-y-3">
                {searchQuery.trim() ? (
                  <div className="p-4 bg-white border border-slate-200 rounded-xl space-y-2.5 shadow-2xs">
                    <div className="w-10 h-10 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center mx-auto">
                      <Send className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="text-xs font-bold text-slate-900">No chat found for "{searchQuery}"</p>
                      <p className="text-[11px] text-slate-500">You can start a new encrypted WhatsApp conversation directly.</p>
                    </div>
                    <button
                      onClick={handleStartChatFromSearch}
                      className="btn-primary text-xs w-full py-2 px-3 flex items-center justify-center gap-1.5"
                    >
                      <Plus className="w-3.5 h-3.5" />
                      <span>Start Chat with {searchQuery}</span>
                    </button>
                  </div>
                ) : (
                  <div className="p-8 text-center text-xs text-slate-400 space-y-2">
                    <p>No conversations found.</p>
                    <button
                      onClick={() => setIsNewChatOpen(true)}
                      className="text-emerald-600 font-bold hover:underline"
                    >
                      + Start a new chat
                    </button>
                  </div>
                )}
              </div>
            ) : (
              filteredConversations.map((conv: Conversation) => {
                const isActive = conv.id === activeConversationId;
                const contactTitle = getContactDisplayName(conv.contact);
                const isMeta = conv.channel_type === "meta_official";

                return (
                  <button
                    key={conv.id}
                    onMouseEnter={() => {
                      queryClient.prefetchQuery({
                        queryKey: ["inbox", "messages", conv.id],
                        queryFn: () => getConversationMessages(token, conv.id),
                        staleTime: 60_000,
                      });
                    }}
                    onClick={() => {
                      setActiveConversationId(conv.id);
                      if (conv.unread_count > 0) {
                        queryClient.setQueriesData({ queryKey: ["inbox", "conversations"] }, (old: any) => {
                          if (!Array.isArray(old)) return old;
                          return old.map((c: Conversation) => (c.id === conv.id ? { ...c, unread_count: 0 } : c));
                        });
                        markConversationRead(token, conv.id);
                      }
                    }}
                    className={`w-full p-3 text-left flex items-start gap-3 transition-colors ${
                      isActive ? "bg-emerald-50/70 border-l-4 border-emerald-600" : "hover:bg-slate-100/70"
                    }`}
                  >
                    <div className="relative">
                      <div className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center font-bold text-slate-700 text-xs shrink-0">
                        {contactTitle.slice(0, 2).toUpperCase()}
                      </div>
                      <span className="absolute -bottom-0.5 -right-0.5 p-0.5 rounded-full bg-white shadow-2xs">
                        {isMeta ? (
                          <ShieldCheck className="w-3 h-3 text-blue-600" />
                        ) : (
                          <QrCode className="w-3 h-3 text-emerald-600" />
                        )}
                      </span>
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex justify-between items-baseline mb-0.5">
                        <span className="font-bold text-slate-900 text-xs truncate pr-1">
                          {contactTitle}
                        </span>
                        <span className="text-[10px] text-slate-400 font-mono shrink-0">
                          {new Date(conv.last_message_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                        </span>
                      </div>

                      <div className="flex justify-between items-center">
                        <p className="text-xs text-slate-500 truncate pr-1">
                          {conv.last_message_preview || "No messages yet"}
                        </p>
                        {conv.unread_count > 0 && (
                          <span className="px-1.5 py-0.5 rounded-full bg-emerald-600 text-white text-[10px] font-bold shrink-0">
                            {conv.unread_count}
                          </span>
                        )}
                      </div>

                      <div className="flex items-center gap-1.5 mt-1">
                        {conv.assigned_user_id === user.id ? (
                          <span className="px-1.5 py-0.5 rounded text-[9px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
                            Mine
                          </span>
                        ) : !conv.assigned_user_id ? (
                          <span className="px-1.5 py-0.5 rounded text-[9px] font-semibold bg-amber-50 text-amber-700 border border-amber-200">
                            Open
                          </span>
                        ) : (
                          <span className="px-1.5 py-0.5 rounded text-[9px] text-slate-600 bg-slate-100 border border-slate-200 truncate max-w-[120px]">
                            @{teamUsers.find((u) => u.id === conv.assigned_user_id)?.username || "Agent"}
                          </span>
                        )}
                      </div>
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>

        {/* Center Column: Active Chat Transcript */}
        <div className={`${!activeConversationId ? "hidden md:flex" : "flex"} flex-1 min-w-0 flex-col h-full min-h-0 bg-white`}>
          {activeConversation ? (
            <>
              {/* Chat Header */}
              <div className="p-2.5 sm:p-3 border-b border-slate-200 flex items-center justify-between bg-white gap-2">
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  {/* Mobile Back Button */}
                  <button
                    type="button"
                    onClick={() => {
                      hasAutoSelectedRef.current = true;
                      setActiveConversationId(null);
                    }}
                    className="md:hidden p-1.5 -ml-1 rounded-lg hover:bg-slate-100 text-slate-700 active:bg-slate-200 transition-colors cursor-pointer shrink-0"
                    title="Back to conversation list"
                  >
                    <ArrowLeft className="w-5 h-5 text-slate-800" />
                  </button>

                  <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-emerald-100 text-emerald-800 font-bold flex items-center justify-center text-xs shrink-0 shadow-2xs">
                    {getContactDisplayName(activeConversation.contact).slice(0, 2).toUpperCase()}
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="font-bold text-slate-900 text-xs flex items-center gap-1.5 truncate">
                      <span className="truncate">{getContactDisplayName(activeConversation.contact)}</span>
                      {activeConversation.channel_type === "meta_official" && (
                        <span className="px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 text-[9px] font-semibold border border-blue-200 shrink-0">
                          Meta
                        </span>
                      )}
                    </h3>
                    <p className="text-[10px] sm:text-[11px] font-mono text-slate-500 truncate">
                      {activeConversation.contact?.phone_e164 || "Active WhatsApp Session"}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-1 sm:gap-2 shrink-0">
                  {/* Assignment Controls */}
                  {activeConversation.assigned_user_id ? (
                    <div className="flex items-center gap-1 bg-slate-50 border border-slate-200 rounded-lg px-2 py-1 text-xs">
                      <UserCheck className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                      <span className="text-slate-600 font-medium text-[10px] sm:text-[11px] hidden sm:inline">
                        {activeConversation.assigned_user_id === user.id ? (
                          <strong className="text-emerald-700">Assigned to You</strong>
                        ) : (
                          <>
                            Assigned:{" "}
                            <strong className="text-slate-800">
                              {teamUsers.find((u) => u.id === activeConversation.assigned_user_id)?.username || "Agent"}
                            </strong>
                          </>
                        )}
                      </span>
                      <button
                        type="button"
                        onClick={() =>
                          assignMutation.mutate({
                            conversationId: activeConversation.id,
                            assignedUserId: null,
                          })
                        }
                        disabled={assignMutation.isPending}
                        title="Release back to open queue"
                        className="text-[10px] text-slate-400 hover:text-rose-600 font-bold ml-0.5 cursor-pointer"
                      >
                        Release
                      </button>
                      {activeConversation.assigned_user_id !== user.id && (
                        <button
                          type="button"
                          onClick={() =>
                            assignMutation.mutate({
                              conversationId: activeConversation.id,
                              assignedUserId: user.id,
                            })
                          }
                          disabled={assignMutation.isPending}
                          className="text-[10px] text-emerald-600 hover:text-emerald-800 font-bold ml-0.5 cursor-pointer"
                        >
                          Claim
                        </button>
                      )}
                    </div>
                  ) : (
                    <div className="flex items-center gap-1">
                      <span className="hidden sm:inline-flex px-2 py-0.5 rounded text-[11px] font-semibold bg-amber-50 text-amber-700 border border-amber-200 items-center gap-1">
                        <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />
                        Unassigned
                      </span>
                      <button
                        type="button"
                        onClick={() =>
                          assignMutation.mutate({
                            conversationId: activeConversation.id,
                            assignedUserId: user.id,
                          })
                        }
                        disabled={assignMutation.isPending}
                        className="px-2 sm:px-2.5 py-1 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-[11px] sm:text-xs font-semibold flex items-center gap-1 shadow-2xs cursor-pointer transition-colors"
                      >
                        <Zap className="w-3 h-3" />
                        <span>Claim</span>
                      </button>
                    </div>
                  )}

                  {/* Manager/Admin Dropdown to Assign Specific Member */}
                  {user.role !== "agent" && teamUsers.length > 0 && (
                    <select
                      value={activeConversation.assigned_user_id || ""}
                      onChange={(e) =>
                        assignMutation.mutate({
                          conversationId: activeConversation.id,
                          assignedUserId: e.target.value || null,
                        })
                      }
                      className="hidden sm:inline-block bg-slate-50 border border-slate-200 text-slate-700 text-[10px] sm:text-[11px] rounded-md px-1.5 py-1 focus:outline-none focus:border-emerald-500 cursor-pointer max-w-[100px]"
                      title="Assign chat to specific team member"
                    >
                      <option value="">-- Assign --</option>
                      {teamUsers.map((member) => (
                        <option key={member.id} value={member.id}>
                          {member.username} ({member.role})
                        </option>
                      ))}
                    </select>
                  )}

                  <span className="status-pill status-pill--connected text-[10px] hidden md:inline-flex">
                    Live
                  </span>
                </div>
              </div>

              {/* Pinned Message Banner */}
              {pinnedMessage && (
                <div
                  onClick={() => scrollToMessage(pinnedMessage.id)}
                  className="px-4 py-2 bg-amber-50/90 border-b border-amber-200/80 flex items-center justify-between text-xs text-amber-900 cursor-pointer hover:bg-amber-100/90 transition-colors shrink-0 shadow-2xs"
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <Pin className="w-3.5 h-3.5 text-amber-600 rotate-45 shrink-0" />
                    <div className="truncate">
                      <span className="font-semibold mr-1">Pinned:</span>
                      <span className="text-amber-800 italic">
                        {pinnedMessage.content?.text || `[${pinnedMessage.message_type}]`}
                      </span>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      pinMutation.mutate(pinnedMessage.id);
                    }}
                    className="text-[10px] text-amber-700 hover:text-amber-900 underline font-medium shrink-0 ml-2"
                  >
                    Unpin
                  </button>
                </div>
              )}

              {/* Message Transcript */}
              <div ref={messagesContainerRef} className="flex-1 p-4 pt-4 pb-6 overflow-y-auto space-y-3 bg-slate-50/40">

                {messagesQuery.isLoading ? (
                  <div className="space-y-3 p-3 animate-pulse">
                    <div className="flex items-start gap-2 max-w-[65%]">
                      <div className="w-8 h-8 rounded-full bg-slate-200 shrink-0"></div>
                      <div className="space-y-1.5 flex-1 pt-1">
                        <div className="h-4 bg-slate-200 rounded-lg w-3/4"></div>
                        <div className="h-3 bg-slate-200/70 rounded-lg w-1/2"></div>
                      </div>
                    </div>
                    <div className="flex items-end justify-end gap-2 ml-auto max-w-[65%]">
                      <div className="space-y-1.5 flex-1 pt-1">
                        <div className="h-4 bg-emerald-200/60 rounded-lg w-2/3 ml-auto"></div>
                      </div>
                    </div>
                    <div className="flex items-start gap-2 max-w-[65%]">
                      <div className="w-8 h-8 rounded-full bg-slate-200 shrink-0"></div>
                      <div className="space-y-1.5 flex-1 pt-1">
                        <div className="h-4 bg-slate-200 rounded-lg w-5/6"></div>
                      </div>
                    </div>
                  </div>
                ) : messages.length === 0 ? (
                  <div className="text-center py-10 text-xs text-slate-400 space-y-1">
                    <p>No messages in this chat yet.</p>
                    <p className="text-[11px]">Type below to send an encrypted WhatsApp message.</p>
                  </div>
                ) : (
                  messages.map((msg: Message) => {
                    const isOutbound = msg.direction === "outbound";
                    const isHighlighted = highlightedMessageId === msg.id;

                    return (
                      <div
                        key={msg.id}
                        id={`msg-${msg.id}`}
                        className={`flex flex-col transition-all duration-300 ${
                          isOutbound ? "items-end" : "items-start"
                        } ${isHighlighted ? "scale-[1.02]" : ""}`}
                      >
                        <div
                          onContextMenu={(e) => {
                            e.preventDefault();
                            setContextMenu({
                              isOpen: true,
                              message: msg,
                              position: { x: Math.max(10, e.clientX - 100), y: e.clientY },
                            });
                          }}
                          className={`group relative max-w-[85%] sm:max-w-[75%] rounded-2xl px-3.5 py-2 text-xs shadow-2xs transition-all ${
                            isHighlighted ? "ring-2 ring-emerald-500 shadow-md " : ""
                          }${
                            isOutbound
                              ? "bg-emerald-600 text-white rounded-br-xs"
                              : "bg-white text-slate-900 border border-slate-200 rounded-bl-xs"
                          }`}
                        >
                          {/* Chevron Menu Trigger on Hover */}
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              const rect = e.currentTarget.getBoundingClientRect();
                              setContextMenu({
                                isOpen: true,
                                message: msg,
                                position: {
                                  x: Math.max(10, isOutbound ? rect.right - 220 : rect.left),
                                  y: rect.bottom + 4,
                                },
                              });
                            }}
                            className={`absolute top-1.5 right-1.5 opacity-0 group-hover:opacity-100 p-0.5 rounded transition-all z-10 cursor-pointer ${
                              isOutbound ? "hover:bg-emerald-700/80 text-emerald-100" : "hover:bg-slate-200/80 text-slate-500"
                            }`}
                            title="Message options"
                          >
                            <ChevronDown className="w-3.5 h-3.5" />
                          </button>

                          {/* Forwarded Indicator */}
                          {msg.content?.is_forwarded && (
                            <div
                              className={`flex items-center gap-1 text-[10px] italic mb-1 ${
                                isOutbound ? "text-emerald-100" : "text-slate-400"
                              }`}
                            >
                              <CornerUpRight className="w-3 h-3" />
                              <span>Forwarded</span>
                            </div>
                          )}

                          {/* Quoted Message Preview (Authentic WhatsApp Web Style) */}
                          {msg.content?.quoted && (
                            <div
                              onClick={() => {
                                if (msg.content?.quoted?.id) {
                                  scrollToMessage(msg.content.quoted.id);
                                }
                              }}
                              className={`mb-2 rounded-lg border-l-4 p-2.5 text-xs cursor-pointer transition-all select-none ${
                                isOutbound
                                  ? "border-emerald-200 bg-emerald-800/30 text-emerald-50 hover:bg-emerald-800/40"
                                  : "border-[#25D366] bg-slate-100 text-slate-800 hover:bg-slate-200/80 shadow-2xs"
                              }`}
                              title="Click to view quoted message"
                            >
                              <div className="font-semibold text-xs mb-0.5 leading-tight">
                                <span className={isOutbound ? "text-emerald-200 font-bold" : "text-emerald-700 font-bold"}>
                                  {msg.content.quoted.from_me || msg.content.quoted.sender === "You" || (!isOutbound && !msg.content.quoted.participant)
                                    ? "You"
                                    : getContactDisplayName(activeConversation?.contact)}
                                </span>
                              </div>
                              <p className={`line-clamp-2 text-xs leading-snug ${isOutbound ? "text-emerald-100" : "text-slate-600"}`}>
                                {msg.content.quoted.text || (msg.content.quoted.message_type ? `[${msg.content.quoted.message_type}]` : "Original message")}
                              </p>
                            </div>
                          )}

                          {/* In-app Media Attachments */}
                          {msg.message_type !== "text" && (
                            <AuthenticatedMedia
                              token={token}
                              message={msg}
                              onPreviewMedia={(media) => setActiveMediaPreview(media)}
                            />
                          )}

                          {/* Message Body / Caption (suppress raw placeholder labels and bracketed filenames) */}
                          {msg.content?.text && (msg.message_type === "text" || !/^\[.*\]$/.test(msg.content.text.trim())) && (
                            <p
                              className={`${
                                msg.message_type !== "text" ? "mt-2" : ""
                              } whitespace-pre-wrap leading-relaxed pr-3`}
                            >
                              {msg.content.text}
                            </p>
                          )}

                          {/* Timestamp, Indicators and Delivery Ticks */}
                          <div
                            className={`flex items-center justify-end gap-1 mt-1 text-[10px] ${
                              isOutbound ? "text-emerald-100" : "text-slate-400"
                            }`}
                          >
                            {msg.content?.is_starred && (
                              <span title="Starred message">
                                <Star className="w-3 h-3 fill-amber-400 text-amber-400 shrink-0" />
                              </span>
                            )}
                            {msg.content?.is_pinned && (
                              <span title="Pinned message">
                                <Pin className="w-3 h-3 text-amber-400 shrink-0 rotate-45" />
                              </span>
                            )}
                            <span className="font-mono">
                              {new Date(msg.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                            </span>
                            {isOutbound && (
                              <span title={`Status: ${msg.status}`}>
                                {msg.status === "read" ? (
                                  <CheckCheck className="w-3.5 h-3.5 text-cyan-300 drop-shadow-xs" />
                                ) : msg.status === "delivered" ? (
                                  <CheckCheck className="w-3.5 h-3.5 text-emerald-200" />
                                ) : msg.status === "sent" ? (
                                  <Check className="w-3.5 h-3.5 text-emerald-200/80" />
                                ) : msg.status === "failed" ? (
                                  <AlertCircle className="w-3.5 h-3.5 text-rose-300" />
                                ) : (
                                  <Clock className="w-3 h-3 text-emerald-200/70" />
                                )}
                              </span>
                            )}
                          </div>

                          {/* Emoji Reaction Pill */}
                          {msg.content?.reactions && msg.content.reactions.length > 0 && (
                            <div
                              onClick={(e) => {
                                e.stopPropagation();
                                const rect = e.currentTarget.getBoundingClientRect();
                                setContextMenu({
                                  isOpen: true,
                                  message: msg,
                                  position: { x: rect.left, y: Math.max(10, rect.top - 120) },
                                });
                              }}
                              className={`absolute -bottom-2.5 ${
                                isOutbound ? "right-2" : "left-2"
                              } bg-white border border-slate-200 rounded-full px-1.5 py-0.5 shadow-xs flex items-center gap-1 cursor-pointer hover:scale-105 transition-transform z-10 text-[11px]`}
                              title={msg.content.reactions.map((r: any) => `${r.sender || "Agent"}: ${r.emoji}`).join(", ")}
                            >
                              <span className="flex items-center gap-0.5">
                                {Array.from(new Set(msg.content.reactions.map((r: any) => r.emoji))).map((emoji: any, idx: number) => (
                                  <span key={idx}>{emoji}</span>
                                ))}
                              </span>
                              {msg.content.reactions.length > 1 && (
                                <span className="text-[9px] font-semibold text-slate-500">
                                  {msg.content.reactions.length}
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Message Composer */}
              <div className="p-3 border-t border-slate-200 bg-white relative">
                {/* Canned Replies Popup */}
                {showCannedReplies && (
                  <div className="absolute bottom-full left-3 right-3 mb-2 bg-white border border-slate-200 rounded-xl shadow-lg p-2 max-h-48 overflow-y-auto space-y-1">
                    <div className="text-[10px] font-bold uppercase text-slate-400 px-2 py-1">
                      Quick Canned Replies
                    </div>
                    {cannedReplies.map((reply) => (
                      <button
                        key={reply.id}
                        onClick={() => insertCannedReply(reply)}
                        className="w-full text-left p-2 rounded-lg hover:bg-slate-50 flex items-center justify-between text-xs"
                      >
                        <div>
                          <span className="font-bold text-slate-800">{reply.title}</span>
                          <p className="text-slate-500 text-[11px] truncate max-w-sm">{reply.content}</p>
                        </div>
                        <span className="font-mono text-[10px] text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded">
                          /{reply.shortcut}
                        </span>
                      </button>
                    ))}
                  </div>
                )}
                <form onSubmit={handleSend} className="space-y-2">
                  {replyingTo && (
                    <div className="flex items-center justify-between rounded-lg border-l-4 border-emerald-500 bg-slate-50 px-3 py-2 text-xs">
                      <div className="min-w-0">
                        <p className="font-bold text-emerald-700">
                          {replyingTo.direction === "outbound" ? "You" : getContactDisplayName(activeConversation?.contact)}
                        </p>
                        <p className="truncate text-slate-500">{replyingTo.content?.text || `[${replyingTo.message_type}]`}</p>
                      </div>
                      <button type="button" onClick={() => setReplyingTo(null)} className="p-1 text-slate-500 hover:text-slate-800 cursor-pointer">
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  )}
                  {attachment && (
                    <div className="flex items-center justify-between rounded-xl border border-emerald-200 bg-emerald-50/80 px-3 py-2 text-xs shadow-2xs">
                      <div className="flex min-w-0 items-center gap-2.5">
                        {attachment.type.startsWith("image/") ? (
                          <ImageIcon className="h-5 w-5 text-emerald-600 shrink-0" />
                        ) : attachment.type.startsWith("video/") ? (
                          <Film className="h-5 w-5 text-purple-600 shrink-0" />
                        ) : attachment.type.startsWith("audio/") ? (
                          <Music className="h-5 w-5 text-blue-600 shrink-0" />
                        ) : (
                          <FileText className="h-5 w-5 text-amber-600 shrink-0" />
                        )}
                        <div className="min-w-0">
                          <span className="truncate font-semibold text-slate-800 block">{attachment.name}</span>
                          <span className="text-[10px] text-slate-500 font-mono">
                            {(attachment.size / 1024 / 1024).toFixed(2)} MB • Add optional caption below
                          </span>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => setAttachment(null)}
                        className="p-1 text-slate-400 hover:text-slate-700 hover:bg-slate-200/60 rounded-lg transition-colors"
                        title="Remove attachment"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  )}
                  <div className="flex items-center justify-between pb-1">
                    <div className="flex items-center gap-1.5">
                      <button
                        type="button"
                        onClick={() => setShowCannedReplies(!showCannedReplies)}
                        className="text-xs text-slate-600 hover:text-emerald-600 flex items-center gap-1 px-2 py-1 rounded bg-slate-100 hover:bg-slate-200 transition-colors font-medium"
                      >
                        <Zap className="w-3 h-3 text-amber-500" />
                        <span>Quick Reply</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => { setShowTemplates(true); setShowCannedReplies(false); }}
                        className="flex items-center gap-1 rounded bg-slate-100 px-2 py-1 text-xs font-medium text-slate-600 hover:bg-slate-200 hover:text-emerald-600 transition-colors"
                      >
                        <LayoutTemplate className="h-3.5 w-3.5" />
                        <span>Template</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => attachmentInputRef.current?.click()}
                        className="p-1 text-slate-400 hover:text-slate-600 rounded hover:bg-slate-100 transition-colors"
                        title="Attach File"
                      >
                        <Paperclip className="w-4 h-4" />
                      </button>
                      <input
                        ref={attachmentInputRef}
                        type="file"
                        accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.zip"
                        className="hidden"
                        onChange={(event) => {
                          const file = event.target.files?.[0] || null;
                          if (file && file.size > 25 * 1024 * 1024) alert("Attachment must be 25 MB or smaller.");
                          else setAttachment(file);
                          event.target.value = "";
                        }}
                      />
                    </div>

                    {/* Active Sending Channel Pill */}
                    <div className="flex items-center gap-1 text-[11px] text-slate-500">
                      <span className="font-semibold text-slate-400">Sending via:</span>
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-100 border border-slate-200 text-slate-700 font-mono text-[10px]">
                        {activeQR ? (
                          <>
                            <Smartphone className="w-3 h-3 text-emerald-600" />
                            <span>{activeQR.name} ({activeQR.phone_number ? `+${activeQR.phone_number}` : "QR"})</span>
                          </>
                        ) : activeMeta ? (
                          <>
                            <ShieldCheck className="w-3 h-3 text-blue-600" />
                            <span>{activeMeta.name}</span>
                          </>
                        ) : (
                          <>
                            <Radio className="w-3 h-3 text-emerald-600" />
                            <span>Auto (Session Channel)</span>
                          </>
                        )}
                      </span>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      placeholder="Type a WhatsApp message..."
                      value={messageInput}
                      onChange={(e) => setMessageInput(e.target.value)}
                      className="flex-1 px-3.5 py-2 rounded-lg border border-slate-300 text-xs focus:outline-none focus:border-emerald-600 shadow-2xs"
                    />
                    <button
                      type="submit"
                      disabled={(!messageInput.trim() && !attachment) || sendMutation.isPending}
                      className="btn-primary py-2 px-3.5 text-xs flex items-center gap-1.5 shadow-2xs"
                    >
                      <Send className="w-3.5 h-3.5" />
                      <span>{sendMutation.isPending ? "Sending..." : "Send"}</span>
                    </button>
                  </div>
                </form>
              </div>
            </>
          ) : (
            <div className="flex-1 flex flex-col items-center justify-center text-slate-400 text-xs space-y-2">
              <Radio className="w-10 h-10 text-slate-300" />
              <p>Select a chat from the left or start a new conversation.</p>
            </div>
          )}
        </div>

        {/* Right Column: Contact Metadata & Notes Drawer (Compact 280-320px) */}
        <div className="hidden xl:flex xl:w-[280px] 2xl:w-[320px] shrink-0 border-l border-slate-200 flex-col h-full min-h-0 bg-slate-50/50 p-4 space-y-4 overflow-y-auto">
          {activeConversation ? (

            <>
              <div className="text-center space-y-2 pb-3 border-b border-slate-200">
                <div className="w-14 h-14 rounded-full bg-emerald-100 text-emerald-800 font-black text-base flex items-center justify-center mx-auto shadow-2xs">
                  {getContactDisplayName(activeConversation.contact).slice(0, 2).toUpperCase()}
                </div>

                {editingContactId === (activeConversation.contact?.id || activeConversation.contact_id) ? (
                  <form
                    onSubmit={(e) => {
                      e.preventDefault();
                      const currentContactId = activeConversation.contact?.id || activeConversation.contact_id;
                      if (!currentContactId) return;
                      updateContactNameMutation.mutate({
                        contactId: currentContactId,
                        name: contactNameInput,
                      });
                    }}
                    className="flex items-center justify-center gap-1.5 px-1 mt-1"
                  >
                    <input
                      type="text"
                      autoFocus
                      value={contactNameInput}
                      onChange={(e) => setContactNameInput(e.target.value)}
                      placeholder="Enter contact name..."
                      className="text-xs font-semibold text-slate-900 px-2.5 py-1 border border-emerald-500 rounded-lg focus:outline-none focus:ring-1 focus:ring-emerald-500 w-full text-center bg-white shadow-2xs"
                      onKeyDown={(e) => {
                        if (e.key === "Escape") {
                          setEditingContactId(null);
                        }
                      }}
                    />
                    <button
                      type="submit"
                      disabled={updateContactNameMutation.isPending}
                      className="p-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white transition-colors shrink-0 shadow-2xs"
                      title="Save Name (Enter)"
                    >
                      {updateContactNameMutation.isPending ? (
                        <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        <Check className="w-3.5 h-3.5" />
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={() => setEditingContactId(null)}
                      disabled={updateContactNameMutation.isPending}
                      className="p-1.5 rounded-lg bg-slate-200 hover:bg-slate-300 text-slate-600 transition-colors shrink-0"
                      title="Cancel (Esc)"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </form>
                ) : (
                  <div className="group flex items-center justify-center gap-1.5 mt-1">
                    <h4
                      className="font-bold text-slate-900 text-xs truncate max-w-[190px] cursor-pointer hover:text-emerald-700 transition-colors"
                      onClick={() => {
                        const targetId = activeConversation.contact?.id || activeConversation.contact_id;
                        const current = getContactDisplayName(activeConversation.contact);
                        setContactNameInput(current === "Unknown" ? "" : current);
                        setEditingContactId(targetId);
                      }}
                      title="Click to change contact name"
                    >
                      {getContactDisplayName(activeConversation.contact)}
                    </h4>
                    <button
                      type="button"
                      onClick={() => {
                        const targetId = activeConversation.contact?.id || activeConversation.contact_id;
                        const current = getContactDisplayName(activeConversation.contact);
                        setContactNameInput(current === "Unknown" ? "" : current);
                        setEditingContactId(targetId);
                      }}
                      className="p-1 rounded-md text-slate-400 hover:text-emerald-600 hover:bg-slate-200/60 transition-colors opacity-70 group-hover:opacity-100"
                      title="Change contact name"
                    >
                      <Edit2 className="w-3 h-3" />
                    </button>
                  </div>
                )}

                <p className="font-mono text-[11px] text-slate-500">
                  {activeConversation.contact?.phone_e164 || "—"}
                </p>
              </div>

              <div className="space-y-3 text-xs">
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                    Channel Protocol
                  </label>
                  <div className="font-semibold text-slate-800 mt-0.5">
                    {activeConversation.channel_type === "meta_official"
                      ? "Official Meta Cloud API (WABA)"
                      : "Multi-Device Direct QR Node"}
                  </div>
                </div>

                <div>
                  <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1">
                    <TagIcon className="w-3 h-3" /> Assigned Tags
                  </label>
                  <div className="flex flex-wrap gap-1 mt-1">
                    <span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 text-[10px] font-semibold">
                      VIP Customer
                    </span>
                    <span className="px-2 py-0.5 rounded-full bg-blue-100 text-blue-800 text-[10px] font-semibold">
                      Lead
                    </span>
                  </div>
                </div>

                <div>
                  <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                    Conversation Status
                  </label>
                  <div className="mt-1">
                    <span className="status-pill status-pill--connected capitalize">
                      {activeConversation.status}
                    </span>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="text-center text-xs text-slate-400 mt-20">
              No conversation active
            </div>
          )}
        </div>
      </div>

      {/* New Chat Modal */}
      {isNewChatOpen && (
        <div className="modal-backdrop" onClick={() => setIsNewChatOpen(false)}>
          <div className="modal-card max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between pb-3 border-b border-slate-200 mb-3">
              <h3 className="text-base font-bold text-slate-900 flex items-center gap-2">
                <User className="w-4 h-4 text-emerald-600" />
                Start New WhatsApp Chat
              </h3>
              <button
                onClick={() => setIsNewChatOpen(false)}
                className="text-slate-400 hover:text-slate-700 text-xs font-bold"
              >
                ✕
              </button>
            </div>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                if (newChatPhone.trim() && !newChatMutation.isPending) {
                  newChatMutation.mutate();
                }
              }}
              className="space-y-3"
            >
              {newChatMutation.isError && (
                <div className="p-2.5 bg-rose-50 border border-rose-200 rounded-lg text-rose-700 text-xs flex items-center justify-between">
                  <span>{(newChatMutation.error as any)?.message || "Failed to start chat. Please check recipient phone number."}</span>
                  <button
                    type="button"
                    onClick={() => newChatMutation.reset()}
                    className="text-rose-500 hover:text-rose-800 font-bold ml-2"
                  >
                    ✕
                  </button>
                </div>
              )}
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  Recipient Phone Number *
                </label>
                <div className="flex items-center gap-2">
                  <select
                    value={selectedCountryCode}
                    onChange={(e) => setSelectedCountryCode(e.target.value)}
                    className="modern-input font-mono text-xs font-semibold py-2 shrink-0 w-36"
                  >
                    {COUNTRY_CODES.map((c) => (
                      <option key={c.code} value={c.code}>
                        {c.flag} {c.code} ({c.country})
                      </option>
                    ))}
                  </select>
                  <input
                    type="text"
                    placeholder="e.g. 7460169503"
                    value={newChatPhone}
                    onChange={(e) => setNewChatPhone(e.target.value)}
                    className="modern-input w-full font-mono text-xs py-2"
                    autoFocus
                  />
                </div>
                <p className="text-[10px] text-slate-400 mt-1">
                  Destination: <span className="font-mono text-slate-700 font-bold">{formatPhoneWithCountryCode(newChatPhone, selectedCountryCode) || `${selectedCountryCode}...`}</span>
                </p>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  Contact Display Name (Optional)
                </label>
                <input
                  type="text"
                  placeholder="e.g. Acme Corp Sales Lead"
                  value={newChatName}
                  onChange={(e) => setNewChatName(e.target.value)}
                  className="modern-input w-full text-xs"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  Sender Channel Type *
                </label>
                <select
                  value={newChatChannel}
                  onChange={(e) => setNewChatChannel(e.target.value as any)}
                  className="modern-input w-full text-xs"
                >
                  <option value="qr_whatsapp">Direct QR Multi-Device WhatsApp Node</option>
                  <option value="meta_official">Official Meta Cloud API (WABA)</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  Initial Message (Optional)
                </label>
                <textarea
                  placeholder="Hello! How can we assist you today?"
                  value={newChatInitialMessage}
                  onChange={(e) => setNewChatInitialMessage(e.target.value)}
                  className="modern-input w-full text-xs h-20"
                />
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  className="btn-secondary text-xs"
                  onClick={() => setIsNewChatOpen(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary text-xs"
                  disabled={!newChatPhone.trim() || newChatMutation.isPending}
                >
                  {newChatMutation.isPending ? "Creating..." : "Start Chat"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Fullscreen Media Preview Modal */}
      {activeMediaPreview && (
        <MediaPreviewModal
          isOpen={!!activeMediaPreview}
          onClose={() => setActiveMediaPreview(null)}
          mediaUrl={activeMediaPreview.url}
          mediaType={activeMediaPreview.type}
          fileName={activeMediaPreview.name}
          fileSize={activeMediaPreview.size}
          mimeType={activeMediaPreview.mime}
        />
      )}

      {/* QR WhatsApp Template Picker Modal */}
      <TemplatePickerModal
        isOpen={showTemplates}
        onClose={() => setShowTemplates(false)}
        templates={templates}
        contactName={activeConversation?.contact?.first_name}
        onSelect={(interpolatedText, sendImmediately) => {
          if (sendImmediately) {
            handleSendWithText(interpolatedText);
          } else {
            setMessageInput(interpolatedText);
          }
        }}
      />

      {/* WhatsApp-Style Message Context Menu */}
      {contextMenu?.isOpen && contextMenu.message && (
        <MessageContextMenu
          message={contextMenu.message}
          position={contextMenu.position}
          onClose={() => setContextMenu(null)}
          onReply={(m) => setReplyingTo(m)}
          onCopy={(text, id) => copyMessageText(text, id)}
          onReact={(m, emoji) => reactMutation.mutate({ messageId: m.id, emoji })}
          onForward={(m) => {
            setForwardModal({ isOpen: true, message: m });
            setContextMenu(null);
          }}
          onTogglePin={(m) => pinMutation.mutate(m.id)}
          onToggleStar={(m) => starMutation.mutate(m.id)}
          onDelete={(m) => {
            if (window.confirm("Delete this message? This action cannot be undone.")) {
              deleteMutation.mutate(m.id);
            }
          }}
        />
      )}

      {/* Forward Message Modal */}
      {forwardModal.isOpen && forwardModal.message && (
        <ForwardMessageModal
          isOpen={forwardModal.isOpen}
          onClose={() => setForwardModal({ isOpen: false, message: null })}
          message={forwardModal.message}
          conversations={conversations}
          onForward={handleForwardMessage}
        />
      )}
    </div>
  );
}
